import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { type EventDetail, type MemberStatus, useEvent, useExclusion } from '../api/events';
import { useMembership } from '../auth/session';
import { Dialog } from '../components/Dialog';
import { ErrorView, Loading } from '../components/Guards';
import { dateLocale } from '../i18n';
import { formatEventDates } from '../lib/eventDates';
import ui from '../styles/ui.module.css';
import styles from './EventPage.module.css';
import { EventRoster } from './EventRoster';

/**
 * Director: "Edit line-up" for one event (`/events/:eventId/lineup`), opened from the event page,
 * where the roster is read-only. The same roster, with "Leave out" (a reason only that singer sees)
 * and "Take back" on each row. Who is in a limited line-up is chosen in the event's settings.
 */
export function EventLineupPage() {
  const { eventId } = useParams();
  const membership = useMembership();
  const event = useEvent(membership?.choirId, eventId);
  if (!membership) return null;
  if (event.isPending) return <Loading />;
  if (event.isError || !event.data) return <ErrorView onRetry={() => void event.refetch()} />;
  return <LineupView choirId={membership.choirId} event={event.data} />;
}

function LineupView({ choirId, event }: { choirId: string; event: EventDetail }) {
  const { t } = useTranslation();
  const exclusion = useExclusion(choirId);
  const [leaving, setLeaving] = useState<MemberStatus | null>(null);

  return (
    <div className={ui.page}>
      <Link to={`/events/${event.id}`} className={styles.back}>
        {t('lineupPage.back')}
      </Link>
      <div className={ui.pageHeader}>
        <div>
          <h1>{t('lineupPage.title')}</h1>
          <div className={ui.subtitle} data-testid="lineup-event">
            {[event.name, formatEventDates(event.startsOn, event.endsOn, dateLocale())].join(' · ')}
          </div>
        </div>
        <div className={styles.actions}>
          <Link to={`/events/${event.id}`} className={`${ui.button} ${ui.primary}`}>
            {t('lineupPage.done')}
          </Link>
        </div>
      </div>
      <p className={ui.muted}>
        {t('lineupPage.lead')}
        {event.limitedLineup && (
          <>
            {' '}
            {t('lineupPage.limited')}{' '}
            <Link to={`/events/${event.id}/edit`}>{t('lineupPage.limitedLink')}</Link>
          </>
        )}
      </p>

      <EventRoster
        event={event}
        editing={{
          busy: exclusion.isPending,
          onLeaveOut: setLeaving,
          onTakeBack: (m) => exclusion.mutate({ eventId: event.id, userId: m.userId, exclude: false }),
        }}
      />

      <LeaveOutDialog
        member={leaving}
        busy={exclusion.isPending}
        onClose={() => setLeaving(null)}
        onConfirm={(reason) =>
          leaving &&
          exclusion.mutate({ eventId: event.id, userId: leaving.userId, exclude: true, reason }, { onSuccess: () => setLeaving(null) })
        }
      />
    </div>
  );
}

function LeaveOutDialog({ member, busy, onClose, onConfirm }: { member: MemberStatus | null; busy: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open={!!member}
      title={t('eventPage.excludeTitle')}
      onClose={() => {
        setReason('');
        onClose();
      }}
      data-testid="leave-out-dialog"
    >
      <p className={styles.noMargin}>{t('eventPage.excludeText', { name: member?.displayName ?? '' })}</p>
      <label className={ui.field}>
        <span className={ui.label}>{t('eventPage.reason')}</span>
        <textarea className={styles.textarea} rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <div className={styles.dialogButtons}>
        <button type="button" className={ui.button} onClick={onClose}>
          {t('eventPage.cancel')}
        </button>
        <button
          type="button"
          className={`${ui.button} ${ui.primary}`}
          disabled={busy}
          onClick={() => {
            onConfirm(reason.trim());
            setReason('');
          }}
        >
          {t('eventPage.excludeConfirm')}
        </button>
      </div>
    </Dialog>
  );
}
